import React, { useState, useEffect } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { Outlet, useLocation, useParams, useNavigate } from 'react-router-dom';
import FarmsNav from './FarmsNav';
import FarmSideBar from './FarmSideBar';
import { useFarmData } from '../context/FarmDataContext';
import { useUserAuth } from '../context/UserAuthContext';
import CustomLoadingScreen from './CustomLoadingScreen';
import { API_BASE_URL } from '../config';

const FarmPageLayout = () => {
    const { farm_id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { logOut, user } = useUserAuth();
    const [menuExpanded, setMenuExpanded] = useState(false);
    const [userData, setUserData] = useState(location.state?.userData || null);
    const [idToken, setIdToken] = useState(location.state?.idToken || null);

    const { farmData, fetchFarmData, loading } = useFarmData();
    const zones = location.state?.zones || [];
    const farmLocations = location.state?.farmLocations;

    const isMobile = useMediaQuery('(max-width: 600px)'); // Detect mobile screens

    // Default start: Jan 1 of current year, end: today
    const now = new Date();
    const jan1 = new Date(now.getFullYear(), 0, 1);
    const [startDate, setStartDate] = useState(jan1);
    const [endDate, setEndDate] = useState(now);

    // Fetch ID Token when the user logs in
    useEffect(() => {
        const fetchIdToken = async () => {
            if (user) {
                try {
                    const token = await user.getIdToken();
                    setIdToken(token);
                } catch (error) {
                    console.log("Error fetching ID token:", error);
                }
            }
        };

        fetchIdToken();
    }, [user]);

    // Set user data from the user object and profile data
    useEffect(() => {
        if (user && !userData) {
            // Use profile data if available, otherwise create basic profile
            const profile = user.profile || {
                id: user.uid || user.id,
                name: user.displayName || user.userName,
                phone_number: user.phoneNumber,
                email: user.email,
                verified: user.emailVerified || user.verified,
                farms: [],
                invited_farms: []
            };
            setUserData(profile);
        }
    }, [user, userData]);

    // Handle user logout
    const handleLogout = async () => {
        try {
            await logOut();
            navigate("/");
        } catch (error) {
            console.log(error.message);
        }
    };

    useEffect(() => {
        if (farm_id) {
            if (!farmData || farmData.farmDetails?.id !== farm_id) {
                fetchFarmData(farm_id);
            }
        }
    }, [farm_id, farmData]);

    if (loading || !farmData || farmData.farmDetails?.id !== farm_id) {
        return (
            <CustomLoadingScreen />
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                height: '100vh',
                overflow: 'hidden'
            }}
        >
            {/* Sidebar for Desktop */}
            {!isMobile ? (
                <Box
                    sx={{
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: menuExpanded ? '200px' : '80px',
                        transition: 'width 0.3s',
                        zIndex: 1201,
                    }}
                >
                    <FarmSideBar
                        onLogout={handleLogout}
                        idToken={idToken}
                        userData={userData}
                        menuExpanded={menuExpanded}
                        setMenuExpanded={setMenuExpanded}
                        zones={zones}
                        farm_id={farm_id}
                    />
                </Box>
            ) : (
                /* Bottom Navigation for Mobile */
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1201,
                    }}
                >
                    <FarmSideBar
                        onLogout={handleLogout}
                        idToken={idToken}
                        userData={userData}
                        menuExpanded={false} // No expansion logic for bottom nav
                        setMenuExpanded={() => {}} // Disabled toggle
                        zones={zones}
                        farm_id={farm_id}
                    />
                </Box>
            )}

            {/* Main Content Wrapper */}
            <Box
                sx={{
                    marginLeft: !isMobile ? '60px' : '0px',
                    width: !isMobile ? 'calc(100% - 60px)' : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    transition: 'margin-left 0.3s, width 0.3s',
                }}
            >
                {/* Top Navigation */}
                <Box
                    sx={{
                        flexShrink: 0,
                        width: '100%',
                        zIndex: 1200,
                        position: 'sticky',
                        top: 0,
                        paddingTop: 3,
                        bgcolor: '#181F2A',
                    }}
                >
                    <FarmsNav
                        farmLocations={farmLocations}
                        farm_id={farm_id}
                        idToken={idToken}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                    />
                </Box>

                {/* Main Content Area */}
                <Box
                    component="main"
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        paddingTop: { xs: '0px', md: '0px' },
                        overflow: 'auto',
                        bgcolor: '#181F2A',
                        width: '100%',
                        maxWidth: '100%',
                        pl: 2,
                    }}
                >
                    <Box sx={{ 
                        width: '100%', 
                        display: 'flex',
                        gap: 0
                    }}>
                        <Outlet context={{ userData, idToken, farm_id, zones, startDate, endDate, setStartDate, setEndDate }} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default FarmPageLayout;